// frontend/src/components/CompanyPermissionRoute.js
import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import usePermission from '../hooks/usePermission';

/**
 * A route guard component that checks if the user has the required permission(s)
 * AND belongs to specific company IDs or has admin role
 *
 * @param {string} application - The application name to check permissions for
 * @param {string|string[]} permission - The required permission(s). Can be a string or array of strings
 * @param {number|number[]} allowedCompanyIds - Company ID(s) allowed to access this route. Can be a number or array of numbers
 * @param {boolean} requireAll - If permission is an array, whether to require ALL permissions (true) or ANY permission (false). Default: false (OR logic)
 * @param {string} fallbackPath - Where to redirect if permission check fails
 * @returns {JSX.Element} The protected route
 */
const CompanyPermissionRoute = ({
  application,
  permission,
  allowedCompanyIds = [],
  requireAll = false,
  fallbackPath = '/permission-denied'
}) => {
  const { user } = useAuth();
  const { hasPermission, permissionsLoading } = usePermission();

  // Normalize allowedCompanyIds to array
  const companyIdsArray = Array.isArray(allowedCompanyIds) ? allowedCompanyIds : [allowedCompanyIds];

  // Admin users bypass all restrictions
  if (user?.role === 'admin') {
    return <Outlet />;
  }

  // Wait for the permission fetch before deciding — see PermissionRoute for why.
  // Placed after the admin bypass so platform staff are never held up by it.
  if (permissionsLoading) return null;

  // Check if the user has the required permission(s)
  let hasRequiredPermission = false;

  if (Array.isArray(permission)) {
    // Multiple permissions
    if (requireAll) {
      // AND logic - require ALL permissions
      hasRequiredPermission = permission.every(perm => hasPermission(application, perm));
    } else {
      // OR logic - require ANY permission (default)
      hasRequiredPermission = permission.some(perm => hasPermission(application, perm));
    }
  } else {
    // Single permission
    hasRequiredPermission = hasPermission(application, permission);
  }

  // Check if user belongs to allowed company
  const userCompanyId = user?.company?.id || user?.companyId || user?.company_id;
  const belongsToAllowedCompany = companyIdsArray.includes(userCompanyId);

  // User must have permission AND belong to allowed company
  const isAllowed = hasRequiredPermission && belongsToAllowedCompany;

  // If allowed, render the child routes, otherwise redirect
  return isAllowed ? <Outlet /> : <Navigate to={fallbackPath} />;
};

export default CompanyPermissionRoute;
